import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue } from '@theme/utilities';

/**
 * @typedef {object} VariantModalPickerRefs
 * @property {HTMLButtonElement} selectorButton - The selector button that opens the modal.
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLButtonElement} closeButton - The close button in the modal.
 * @property {HTMLElement} modalContent - The modal content container.
 * @property {HTMLButtonElement[]} variantCards - Array of variant card buttons.
 */

/**
 * A custom element that manages a modal-based variant picker.
 *
 * @extends DialogComponent<VariantModalPickerRefs>
 */
export class VariantModalPicker extends DialogComponent {
  requiredRefs = ['selectorButton', 'dialog', 'closeButton', 'modalContent', 'variantCards'];

  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {string | null} */
  #previousFocusedElement = null;

  connectedCallback() {
    super.connectedCallback();

    const { selectorButton, dialog, closeButton, variantCards } = this.refs;

    // Set up selector button click handler
    selectorButton?.addEventListener('click', this.#handleSelectorClick);

    // Set up close button handler
    closeButton?.addEventListener('click', this.#handleClose);

    // Set up variant card click handlers
    variantCards?.forEach((card) => {
      card.addEventListener('click', this.#handleVariantCardClick);
    });

    // Listen for dialog close events
    dialog?.addEventListener('close', this.#handleDialogClose);

    // Listen for variant updates from other pickers
    this.addEventListener('variant:update', this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    removeTrapFocus();
    const { selectorButton, closeButton, variantCards } = this.refs;

    selectorButton?.removeEventListener('click', this.#handleSelectorClick);
    closeButton?.removeEventListener('click', this.#handleClose);
    variantCards?.forEach((card) => {
      card.removeEventListener('click', this.#handleVariantCardClick);
    });
  }

  /**
   * Handles the selector button click to open the modal.
   */
  #handleSelectorClick = () => {
    this.showDialog();
  };

  /**
   * Handles closing the modal.
   */
  #handleClose = () => {
    this.closeDialog();
  };

  /**
   * Handles dialog close event to restore focus.
   */
  #handleDialogClose = () => {
    const { selectorButton } = this.refs;
    removeTrapFocus();
    
    // Restore focus to selector button
    if (this.#previousFocusedElement) {
      this.#previousFocusedElement.focus();
      this.#previousFocusedElement = null;
    } else if (selectorButton) {
      selectorButton.focus();
    }
  };

  /**
   * Handles variant card click to select a variant.
   * @param {MouseEvent} event - The click event.
   */
  #handleVariantCardClick = (event) => {
    const card = event.currentTarget;
    if (!(card instanceof HTMLElement)) return;

    // Don't proceed if variant is unavailable
    if (card.getAttribute('aria-disabled') === 'true') {
      return;
    }

    const optionValueId = card.dataset.optionValueId;
    const variantId = card.dataset.variantId;

    if (!optionValueId) return;

    // Update selected state visually
    this.#updateSelectedCard(card);

    // Dispatch variant selected event
    this.dispatchEvent(new VariantSelectedEvent({ id: optionValueId }));

    // Build request URL
    const requestUrl = this.#buildRequestUrl(card);

    // Fetch updated section
    this.#fetchUpdatedSection(requestUrl);

    // Update URL if on product page
    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !this.closest('product-card') &&
      !this.closest('quick-add-dialog');

    if (isOnProductPage && variantId) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      if (url.href !== window.location.href) {
        yieldToMainThread().then(() => {
          history.replaceState({}, '', url.toString());
        });
      }
    }

    // Close the modal
    this.closeDialog();
  };

  /**
   * Updates the selected card visually.
   * @param {HTMLElement} selectedCard - The card that was selected.
   */
  #updateSelectedCard(selectedCard) {
    const { variantCards } = this.refs;
    if (!variantCards) return;

    // Remove selected state from all cards
    variantCards.forEach((card) => {
      card.classList.remove('variant-modal-picker__card--selected');
      card.removeAttribute('aria-current');
      const checkmark = card.querySelector('.variant-modal-picker__card-checkmark');
      if (checkmark) {
        checkmark.remove();
      }
    });

    // Add selected state to clicked card
    selectedCard.classList.add('variant-modal-picker__card--selected');
    selectedCard.setAttribute('aria-current', 'true');

    // Add checkmark if not present
    if (!selectedCard.querySelector('.variant-modal-picker__card-checkmark')) {
      const checkmark = document.createElement('span');
      checkmark.className = 'variant-modal-picker__card-checkmark';
      checkmark.setAttribute('aria-hidden', 'true');
      checkmark.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
      selectedCard.appendChild(checkmark);
    }

    // Update selector button text and preview
    this.#updateSelectorButton(selectedCard);
  }

  /**
   * Updates the selector button with the selected variant information.
   * @param {HTMLElement} selectedCard - The selected variant card.
   */
  #updateSelectorButton(selectedCard) {
    const { selectorButton } = this.refs;
    if (!selectorButton) return;

    const cardName = selectedCard.querySelector('.variant-modal-picker__card-name');
    const selectedValue = cardName?.textContent?.trim() || '';

    const selectedValueSpan = selectorButton.querySelector('.variant-modal-picker__trigger-value');
    if (selectedValueSpan) {
      selectedValueSpan.textContent = selectedValue;
    }

    // Update preview image if present
    const cardImage = selectedCard.querySelector('.variant-modal-picker__card-image img');
    const previewImage = selectorButton.querySelector('.variant-modal-picker__trigger-preview img');
    if (cardImage && previewImage) {
      previewImage.src = cardImage.src;
      previewImage.srcset = cardImage.srcset;
      previewImage.alt = selectedValue;
    }
  }

  /**
   * Handles variant update events from other pickers.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Update the variant picker HTML if provided
    if (event.detail?.data?.html) {
      this.#updateVariantPicker(event.detail.data.html);
    }
  };

  /**
   * Updates the variant picker with new HTML.
   * @param {Document | string} html - The new HTML content (string) or parsed document.
   */
  #updateVariantPicker(html) {
    /** @type {Document} */
    const doc = html instanceof Document ? html : new DOMParser().parseFromString(html, 'text/html');
    const newPicker = doc.querySelector('variant-modal-picker');

    if (!newPicker) return;

    // Morph the variant cards
    const { variantCards } = this.refs;
    const newCards = Array.from(newPicker.querySelectorAll('.variant-modal-picker__card'));

    if (variantCards && newCards.length === variantCards.length) {
      variantCards.forEach((oldCard, index) => {
        const newCard = newCards[index];
        if (newCard) {
          morph(oldCard, newCard.outerHTML, MORPH_OPTIONS);
        }
      });

      // Re-attach event listeners
      const updatedCards = this.querySelectorAll('.variant-modal-picker__card');
      updatedCards.forEach((card) => {
        card.addEventListener('click', this.#handleVariantCardClick);
      });
    }
  }

  /**
   * Builds the request URL for fetching updated section content.
   * @param {HTMLElement} selectedOption - The selected option element.
   * @returns {string} The request URL.
   */
  #buildRequestUrl(selectedOption) {
    const productUrl = this.dataset.productUrl?.split('?')[0];
    if (!productUrl) return '';

    const url = new URL(productUrl, window.location.origin);
    const variantId = selectedOption.dataset.variantId;

    if (variantId) {
      url.searchParams.set('variant', variantId);
    }

    const sectionId = this.dataset.sectionId;
    if (sectionId) {
      url.searchParams.set('section_id', sectionId);
    }

    return url.href;
  }

  /**
   * Fetches and updates the section content.
   * @param {string} requestUrl - The URL to fetch from.
   */
  #fetchUpdatedSection(requestUrl) {
    if (!requestUrl) return;

    // Cancel any pending request
    if (this.#abortController) {
      this.#abortController.abort();
    }

    this.#abortController = new AbortController();
    this.#pendingRequestUrl = requestUrl;

    fetch(requestUrl, {
      signal: this.#abortController.signal,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((html) => {
        if (this.#pendingRequestUrl !== requestUrl) return;

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const sectionId = this.dataset.sectionId;

        if (sectionId) {
          const sectionSelector = `#shopify-section-${sectionId}, #${sectionId}`;
          const newSection = doc.querySelector(sectionSelector);
          const currentSection = document.querySelector(sectionSelector);

          if (newSection && currentSection) {
            // Variant JSON aus dem frisch gerenderten HTML lesen (kommt aus snippet/variant-modal-picker.liquid)
            let variantResource = null;
            const variantJson = newSection.querySelector('variant-modal-picker script[type="application/json"]')
              ?.textContent;
            if (variantJson) {
              try {
                variantResource = JSON.parse(variantJson);
              } catch (error) {
                console.warn('Could not parse variant JSON from updated section', error);
              }
            }

            const selectedOptionId =
              this.querySelector('.variant-modal-picker__card[aria-current="true"]')?.dataset.optionValueId ?? '';

            // Dispatch variant update event – Formular, Preis, Media-Gallery reagieren darauf.
            // Kein Section-Morph, damit der Slider/Media-Gallery nicht verschwindet.
            this.dispatchEvent(
              new VariantUpdateEvent(variantResource ?? { id: '' }, selectedOptionId, {
                html: doc,
                productId: this.dataset.productId ?? '',
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Error fetching updated section:', error);
        }
      })
      .finally(() => {
        if (this.#pendingRequestUrl === requestUrl) {
          this.#pendingRequestUrl = undefined;
        }
      });
  }

  /**
   * Override showDialog to add focus trap and update aria attributes.
   */
  showDialog() {
    const { dialog, selectorButton, closeButton } = this.refs;
    if (!dialog || !selectorButton) return;

    // Store the previously focused element
    this.#previousFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Update aria attributes
    selectorButton.setAttribute('aria-expanded', 'true');

    // Call parent showDialog (this handles body scroll lock and shows modal)
    super.showDialog();

    // Set up focus trap after dialog is shown
    requestAnimationFrame(() => {
      const { modalContent } = this.refs;
      if (modalContent) {
        trapFocus(modalContent);
      } else if (closeButton) {
        closeButton.focus();
      }
    });
  }

  /**
   * Override closeDialog to update aria attributes.
   */
  async closeDialog() {
    const { selectorButton } = this.refs;
    if (selectorButton) {
      selectorButton.setAttribute('aria-expanded', 'false');
    }

    await super.closeDialog();
  }
}

if (!customElements.get('variant-modal-picker')) {
  customElements.define('variant-modal-picker', VariantModalPicker);
}
